import React, { useEffect, useRef, useState } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { useDarkMode } from '../contexts/DarkModeContext';

// Drop heavy react-icons: inline tiny SVGs
const Icon = {
  Store: (p) => (<svg viewBox="0 0 24 24" width="1em" height="1em" {...p}><path fill="currentColor" d="M4 4h16l1 4H3l1-4zm0 6h16v10H4V10zm4 2v6h2v-6H8zm6 0v6h2v-6h-2z"/></svg>),
  User: (p) => (<svg viewBox="0 0 24 24" width="1em" height="1em" {...p}><path fill="currentColor" d="M12 12a5 5 0 1 0-5-5 5 5 0 0 0 5 5zm0 2c-5 0-9 2.5-9 5v1h18v-1c0-2.5-4-5-9-5z"/></svg>),
  Chat: (p) => (<svg viewBox="0 0 24 24" width="1em" height="1em" {...p}><path fill="currentColor" d="M21 6H3v12h4v4l4-4h10z"/></svg>),
  Filter: (p) => (<svg viewBox="0 0 24 24" width="1em" height="1em" {...p}><path fill="currentColor" d="M10 18h4v-2h-4v2zm-7-12v2h18V6H3zm3 7h12v-2H6v2z"/></svg>)
};

const navItems = [
  {
    label: 'Marketplace',
    to: '/marketplace',
    icon: <Icon.Store className="w-6 h-6" />,
  },
  {
    label: 'My Account',
    to: '/profile',
    icon: <Icon.User className="w-6 h-6" />,
  },
  {
    label: 'Chat',
    to: '/messages',
    icon: <Icon.Chat className="w-6 h-6" />,
    isChat: true,
  },
];

const MobileFooterBar = () => {
  const location = useLocation();
  const { isDarkMode } = useDarkMode();
  const [unreadTotal, setUnreadTotal] = useState(typeof window !== 'undefined' && typeof window.__unreadTotal === 'number' ? window.__unreadTotal : 0);
  const [justIncreased, setJustIncreased] = useState(false);
  const [isChatOpen, setIsChatOpen] = useState(false);
  const prevUnreadRef = useRef(unreadTotal || 0);
  const path = location.pathname;
  const isFilterPage = (
    path === '/marketplace' || path.startsWith('/marketplace') ||
    path === '/buy-youtube-channel' || path.startsWith('/buy-youtube-channel') ||
    path === '/buy-tiktok-account' || path.startsWith('/buy-tiktok-account') ||
    path === '/buy-facebook-page' || path.startsWith('/buy-facebook-page') ||
    path === '/buy-instagram-account' || path.startsWith('/buy-instagram-account')
  );
  const isProductPage = path.startsWith('/product/');
  
  // Listen for unread total broadcast from Header (must be before any early return)
  useEffect(() => {
    const handler = (e) => {
      const next = typeof e.detail === 'number' ? e.detail : 0;
      const prev = prevUnreadRef.current || 0;
      if (next > prev) {
        setJustIncreased(true);
        setTimeout(() => setJustIncreased(false), 700);
      }
      prevUnreadRef.current = next;
      setUnreadTotal(next);
    };
    window.addEventListener('unreadTotalChanged', handler);
    // Initialize on mount
    if (typeof window.__unreadTotal === 'number') {
      handler({ detail: window.__unreadTotal });
    }
    return () => window.removeEventListener('unreadTotalChanged', handler);
  }, []);

  // Listen for chat state changes
  useEffect(() => {
    const handleChatOpen = () => setIsChatOpen(true);
    const handleChatClose = () => setIsChatOpen(false);
    
    window.addEventListener('chatOpened', handleChatOpen);
    window.addEventListener('chatClosed', handleChatClose);
    
    return () => {
      window.removeEventListener('chatOpened', handleChatOpen);
      window.removeEventListener('chatClosed', handleChatClose);
    };
  }, []);

  // Hide the footer on messages page or when chat is open
  if (location.pathname === '/messages' || location.pathname.startsWith('/messages') || isChatOpen) {
    return null;
  }

  return (
    <nav className="fixed bottom-0 left-0 right-0 z-50 md:hidden w-full">
      <div
        className={`flex justify-between items-center ${isProductPage ? '' : 'rounded-t-2xl'} shadow-xl px-2 py-1 relative backdrop-blur ${
          isDarkMode ? 'bg-[#1a1a1a]/95 border border-gray-800' : 'bg-white/95'
        }`}
      >
        {navItems.map((item) => {
          const isActive = location.pathname.startsWith(item.to);
          
          // Handle chat button differently - open popup instead of navigation
          if (item.isChat) {
            return (
              <button
                key={item.to}
                onClick={() => {
                  // Dispatch custom event to open chat popup
                  window.dispatchEvent(new CustomEvent('openChatPopup'));
                }}
                className={`flex flex-col items-center flex-1 py-1 transition group ${
                  isActive
                    ? ''
                    : isDarkMode
                    ? 'text-gray-400'
                    : 'text-gray-500'
                }`}
              >
                <div
                  className={`flex items-center justify-center w-10 h-8 rounded-xl transition-all duration-200 relative ${
                    isActive
                      ? 'bg-red-600 text-white shadow-lg'
                      : isDarkMode
                      ? 'bg-transparent text-gray-400 group-hover:text-red-400'
                      : 'bg-transparent text-gray-500 group-hover:text-red-500'
                  } ${justIncreased ? 'chat-icon-pulse' : (unreadTotal > 0 ? 'chat-icon-attention' : '')}`}
                >
                  {item.icon}
                  {unreadTotal > 0 && (
                    <span className={`absolute -top-1 -right-1 min-w-[20px] h-[20px] px-1.5 inline-flex items-center justify-center rounded-full bg-red-600 text-white text-[11px] font-bold shadow-md ring-2 ring-white ${justIncreased ? 'badge-pop' : 'badge-attention'}`}>
                      {unreadTotal > 99 ? '99+' : unreadTotal}
                    </span>
                  )}
                </div>
                <span
                  className={`text-xs mt-1 font-medium ${
                    isActive
                      ? isDarkMode
                        ? 'text-red-400'
                        : 'text-red-600'
                      : isDarkMode
                      ? 'text-gray-400 group-hover:text-red-400'
                      : 'text-gray-500 group-hover:text-red-500'
                  }`}
                >
                  {item.label}
                </span>
              </button>
            );
          }
          
          // Regular navigation items
          return (
            <Link
              key={item.to}
              to={item.to}
              className={`flex flex-col items-center flex-1 py-1 transition group ${
                isActive
                  ? ''
                  : isDarkMode
                  ? 'text-gray-400'
                  : 'text-gray-500'
              }`}
            >
              <div
                className={`flex items-center justify-center w-10 h-8 rounded-xl transition-all duration-200 relative ${
                  isActive
                    ? 'bg-red-600 text-white shadow-lg'
                    : isDarkMode
                    ? 'bg-transparent text-gray-400 group-hover:text-red-400'
                    : 'bg-transparent text-gray-500 group-hover:text-red-500'
                }`}
              >
                {item.icon}
              </div>
              <span
                className={`text-xs mt-1 font-medium ${
                  isActive
                    ? isDarkMode
                      ? 'text-red-400'
                      : 'text-red-600'
                    : isDarkMode
                    ? 'text-gray-400 group-hover:text-red-400'
                    : 'text-gray-500 group-hover:text-red-500'
                }`}
              >
                {item.label}
              </span>
            </Link>
          );
        })}
        {isFilterPage && (
          <button
            type="button"
            onClick={() => window.dispatchEvent(new Event('openMobileFilters'))}
            className={`flex flex-col items-center flex-1 py-1 transition group ${
              isDarkMode ? 'text-gray-400 group-hover:text-red-400' : 'text-gray-500 group-hover:text-red-500'
            }`}
            aria-label="Open filters"
          >
            <div
              className={`flex items-center justify-center w-10 h-8 rounded-xl transition-all duration-200 ${
                isDarkMode ? 'bg-transparent text-gray-400 group-hover:text-red-400' : 'bg-transparent text-gray-500 group-hover:text-red-500'
              }`}
            >
              <Icon.Filter className="w-5 h-5" />
            </div>
            <span className={`text-xs mt-1 font-medium ${
              isDarkMode ? 'text-gray-400 group-hover:text-red-400' : 'text-gray-500 group-hover:text-red-500'
            }`}>Filters</span>
          </button>
        )}
      </div>
    </nav>
  );
};

export default MobileFooterBar; 